/* Project enquiries: consented interactions, with native confirmation before counting a lead. */
(() => {
  function start() {
    const root = document.documentElement;
    if (root.hasAttribute('data-edu-project-analytics')) return;
    root.setAttribute('data-edu-project-analytics', 'ready');

    const views = {
      proyectos: 'general',
      'proyectos-gobierno': 'gobierno',
      'proyectos-empresas': 'empresas',
      distribucion: 'distribucion'
    };
    const pendingKey = 'educatodo.projectPending.v1';
    const contactPath = '/pages/contacto-educatodo';
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const form = document.getElementById('EducatodoProjectForm');
    const audienceValue = form?.closest('[data-edu-project-audience]')?.getAttribute('data-edu-project-audience');
    const audience = Object.values(views).includes(audienceValue) ? audienceValue : null;
    // This marker must already be in the server-rendered form on this document's arrival.
    const successOnArrival = !!form?.querySelector('[data-edu-project-success]');
    let landingHandled = false;
    let formStarted = false;
    let privacyRequested = false;
    let confirmationRetryTimer = null;
    let confirmationRetryUntil = 0;

    function allowed() {
      try {
        return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
      } catch (_) { return false; }
    }

    function emit(name, details) {
      if (!allowed() || typeof window.gtag !== 'function') return false;
      try {
        window.gtag('event', name, {
          ...details,
          page_location: window.location.origin + path,
          page_referrer: '',
          send_to: 'G-Y12PGNJ1VL'
        });
        return true;
      } catch (_) { return false; }
    }

    function clearPending() {
      if (!allowed()) return false;
      try {
        window.sessionStorage.removeItem(pendingKey);
        return window.sessionStorage.getItem(pendingKey) === null;
      } catch (_) { return false; }
    }

    function stopConfirmationRetry() {
      if (confirmationRetryTimer !== null) window.clearTimeout(confirmationRetryTimer);
      confirmationRetryTimer = null;
    }

    function finishLanding() {
      landingHandled = true;
      stopConfirmationRetry();
    }

    function confirmLanding() {
      if (landingHandled || !form || !audience || path !== contactPath || !allowed()) {
        stopConfirmationRetry();
        return;
      }
      if (!successOnArrival) {
        clearPending();
        finishLanding();
        return;
      }
      let pending;
      try {
        pending = JSON.parse(window.sessionStorage.getItem(pendingKey));
      } catch (_) {
        clearPending();
        finishLanding();
        return;
      }
      const age = Date.now() - pending?.at;
      if (!pending || pending.audience !== audience || pending.path !== path || !Number.isFinite(age) || age < 0 || age > 30 * 60 * 1000) {
        clearPending();
        finishLanding();
        return;
      }
      if (confirmationRetryUntil && Date.now() >= confirmationRetryUntil) {
        clearPending();
        finishLanding();
        return;
      }
      if (typeof window.gtag !== 'function') {
        // Wait briefly for a late Google tag, only for an already eligible confirmation.
        if (!confirmationRetryUntil) confirmationRetryUntil = Date.now() + 30 * 1000;
        if (confirmationRetryTimer === null) {
          confirmationRetryTimer = window.setTimeout(() => {
            confirmationRetryTimer = null;
            confirmLanding();
          }, Math.min(500, confirmationRetryUntil - Date.now()));
        }
        return;
      }
      // Consume before sending: reloads cannot turn one enquiry into several leads.
      finishLanding();
      if (clearPending()) emit('generate_lead', { audience, form_id: 'EducatodoProjectForm' });
    }

    function loadPrivacy() {
      const shopify = window.Shopify;
      if (typeof shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') {
        confirmLanding();
        return;
      }
      if (privacyRequested || typeof shopify?.loadFeatures !== 'function') return;
      privacyRequested = true;
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => {
          if (!error) confirmLanding();
        });
      } catch (_) {}
    }

    function targetOf(event) {
      return event.target instanceof Element ? event.target : event.target?.parentElement;
    }

    function placementOf(anchor) {
      if (anchor.closest('.site-header, #shopify-section-static-header')) return 'header';
      if (anchor.closest('footer, .site-footer-wrapper')) return 'footer';
      if (anchor.closest('.edu-inst__hero')) return 'institutional_hero';
      if (anchor.closest('.edu-inst__close')) return 'institutional_close';
      if (path === '/pages/catalogs') return 'catalogs';
      if (path === '/') return 'home';
      return 'content';
    }

    function trackProposal(event) {
      if (!event.isTrusted || (event.type === 'click' ? event.button !== 0 : event.button !== 1)) return;
      const anchor = targetOf(event)?.closest('a[href]');
      if (!anchor) return;
      let destination;
      try { destination = new URL(anchor.getAttribute('href'), window.location.href); } catch (_) { return; }
      if (destination.origin !== window.location.origin || destination.username || destination.password || destination.pathname.replace(/\/$/, '') !== contactPath) return;
      const view = destination.searchParams.get('view');
      if (!Object.prototype.hasOwnProperty.call(views, view)) return;
      // An unavailable/denied API never queues an old click for later consent.
      if (allowed()) emit('project_proposal_click', { audience: views[view], placement: placementOf(anchor) });
      loadPrivacy();
    }

    function trackStart(event) {
      if (formStarted || !event.isTrusted || !form || !audience || successOnArrival || path !== contactPath) return;
      const field = targetOf(event)?.closest('input, textarea, select');
      if (!field || field.form !== form || field.disabled || ['hidden', 'button', 'submit', 'reset', 'file', 'password'].includes(field.type)) return;
      // Only emptiness is checked; no field content is retained or sent.
      if (typeof field.value !== 'string' || !field.value.trim()) return;
      if (allowed() && emit('project_form_start', { audience, form_id: 'EducatodoProjectForm' })) formStarted = true;
      loadPrivacy();
    }

    function rememberSubmit(event) {
      if (!event.isTrusted || event.target !== form || !audience || successOnArrival || path !== contactPath || !allowed()) return;
      if (event.defaultPrevented || !form.matches(':valid')) {
        clearPending();
        return;
      }
      try {
        window.sessionStorage.setItem(pendingKey, JSON.stringify({ at: Date.now(), audience, path }));
      } catch (_) {}
    }

    document.addEventListener('click', trackProposal);
    document.addEventListener('auxclick', trackProposal);
    document.addEventListener('input', trackStart);
    document.addEventListener('change', trackStart);
    document.addEventListener('invalid', event => {
      if (event.isTrusted && event.target?.form === form) clearPending();
    }, true);
    // Observe after the document's native form/captcha handlers, without altering submission.
    window.addEventListener('submit', rememberSubmit);
    document.addEventListener('visitorConsentCollected', confirmLanding);
    window.addEventListener('load', loadPrivacy, { once: true });
    window.addEventListener('pagehide', stopConfirmationRetry, { once: true });
    loadPrivacy();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
